import React, { ReactElement, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Placement } from '@floating-ui/react';
import classNames from 'classnames';
import DropdownArrow from '@/assets/caretDownLight.svg';
import { PopoverMenu } from '../index';

export type FilterableDropdownTreeOption = {
  label: string;
  value: string;
  disabled?: boolean;
  options?: FilterableDropdownTreeOption[];
};

export interface FilterableDropdownTreeProps {
  name: string;
  options: FilterableDropdownTreeOption[];
  value?: string;
  placeholder?: string;
  onChange?: (event: { target: { name: string; value: string | null; label: string | null } }) => void;
  onBlur?: (event: { target: { name: string; value: string | null; label: string | null } }) => void;
  onClear?: () => void;
  className?: string;
  invalid?: boolean;
  offset?: number;
  placement?: Placement;
  popoverMenuPortal?: boolean;
  contentWrapperClassName?: string;
}

type NormalizedDropdownTreeOption = Omit<FilterableDropdownTreeOption, 'options'> & {
  displayLabel: string;
  searchLabel: string;
  options?: NormalizedDropdownTreeOption[];
  disabled?: boolean;
};

const stripHtml = (value: string) => value?.replace(/<(p)>/g, '').replace(/<\/(p)>/g, '') || '';

const normalizeOptions = (options: FilterableDropdownTreeOption[]): NormalizedDropdownTreeOption[] =>
  options.map((option) => ({
    ...option,
    displayLabel: stripHtml(option.label),
    searchLabel: stripHtml(option.label).toLowerCase(),
    options: option.options ? normalizeOptions(option.options) : undefined,
    disabled: option.disabled,
  }));

const findOption = (
  options: NormalizedDropdownTreeOption[],
  value: string | null,
): NormalizedDropdownTreeOption | undefined => {
  if (value == null) {
    return undefined;
  }

  for (const option of options) {
    if (option.value === value) {
      return option;
    }

    const childOption = option.options ? findOption(option.options, value) : undefined;

    if (childOption) {
      return childOption;
    }
  }

  return undefined;
};

const findExpandedAncestors = (options: NormalizedDropdownTreeOption[], value: string | null): Set<string> => {
  const expandedItems = new Set<string>();

  const visit = (option: NormalizedDropdownTreeOption, ancestors: string[]): boolean => {
    if (option.value === value) {
      ancestors.forEach((ancestor) => expandedItems.add(ancestor));

      if (option.options?.length) {
        expandedItems.add(option.value);
      }

      return true;
    }

    const childHasValue = option.options?.some((child) => visit(child, [...ancestors, option.value])) ?? false;

    return childHasValue;
  };

  options.forEach((option) => visit(option, []));
  return expandedItems;
};

const filterOptions = (options: NormalizedDropdownTreeOption[], searchTerm: string): NormalizedDropdownTreeOption[] => {
  if (!searchTerm) {
    return options;
  }

  return options.flatMap((option) => {
    const childOptions = option.options ? filterOptions(option.options, searchTerm) : undefined;
    const matchesSearchTerm = option.searchLabel.includes(searchTerm);

    if (matchesSearchTerm) {
      return [
        {
          ...option,
          options: childOptions,
        },
      ];
    }

    return childOptions?.length
      ? [
          {
            ...option,
            options: childOptions,
          },
        ]
      : [];
  });
};

const findExpandableItems = (options: NormalizedDropdownTreeOption[]): Set<string> => {
  const expandedItems = new Set<string>();

  const visit = (option: NormalizedDropdownTreeOption) => {
    if (option.options?.length) {
      expandedItems.add(option.value);
      option.options.forEach(visit);
    }
  };

  options.forEach(visit);
  return expandedItems;
};

type RenderOptionsParams = {
  options: NormalizedDropdownTreeOption[];
  selectedValue: string | null;
  expandedItems: Set<string>;
  open: boolean;
  onSelect: (option: NormalizedDropdownTreeOption) => void;
  onToggleExpand: (value: string) => void;
  depth?: number;
};

const renderOptions = ({
  options,
  selectedValue,
  expandedItems,
  open,
  onSelect,
  onToggleExpand,
  depth = 0,
}: RenderOptionsParams): ReactElement => (
  <div className="flex flex-col">
    {options.map((option) => {
      const hasChildren = Boolean(option.options?.length);
      const isExpanded = expandedItems.has(option.value);
      const isSelected = selectedValue === option.value;
      const indentStyle = depth > 0 ? { paddingLeft: Math.min(depth * 18, 72) } : undefined;

      return (
        <div key={option.value}>
          <div className="flex items-center pr-2" style={indentStyle}>
            {hasChildren ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleExpand(option.value);
                }}
                className="ml-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
                aria-label={isExpanded ? `Collapse ${option.displayLabel}` : `Expand ${option.displayLabel}`}
                aria-expanded={isExpanded}
              >
                <DropdownArrow
                  className={classNames('h-4 w-4 fill-current transition-transform duration-200', {
                    'rotate-0': isExpanded,
                    '-rotate-90': !isExpanded,
                  })}
                />
              </button>
            ) : (
              <div className="ml-2 h-8 w-8 flex-shrink-0" />
            )}

            <button
              type="button"
              onClick={() => onSelect(option)}
              disabled={option.disabled}
              className={classNames(
                'tree-option flex min-w-0 flex-1 items-center gap-3 rounded-[6px] px-2 py-2 text-left text-sm transition-colors outline-none',
                isSelected && 'bg-gray-200',

                'cursor-pointer text-zinc-800 hover:bg-gray-100 focus:bg-gray-100',
              )}
              tabIndex={open ? 0 : -1}
            >
              {!option.disabled && (
                <span
                  aria-hidden
                  className={classNames(
                    'flex h-5.5 w-5.5 flex-shrink-0 items-center justify-center rounded-full border-2 border-zinc-950 transition-colors',
                  )}
                >
                  {isSelected && <span className="h-2 w-2 rounded-full bg-zinc-950" />}
                </span>
              )}
              <span className="label text-base leading-5">{option.displayLabel}</span>
            </button>
          </div>

          {hasChildren && isExpanded && (
            <div className="overflow-hidden">
              {renderOptions({
                options: option.options!,
                selectedValue,
                expandedItems,
                open,
                onSelect,
                onToggleExpand,
                depth: depth + 1,
              })}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const FilterableDropdownTree = ({
  name,
  options,
  value,
  placeholder,
  onChange,
  onBlur,
  onClear,
  className,
  invalid,
  offset,
  placement,
  popoverMenuPortal,
  contentWrapperClassName,
}: FilterableDropdownTreeProps) => {
  const [open, setOpen] = useState(false);
  const [_value, set_value] = useState<string | null>(value ?? null);
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const wrapper = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const selectedOption = useMemo(() => findOption(normalizedOptions, _value), [normalizedOptions, _value]);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() =>
    findExpandedAncestors(normalizedOptions, value ?? null),
  );
  const normalizedSearchTerm = useMemo(() => deferredSearchTerm.trim().toLowerCase(), [deferredSearchTerm]);
  const filteredOptions = useMemo(
    () => filterOptions(normalizedOptions, normalizedSearchTerm),
    [normalizedOptions, normalizedSearchTerm],
  );
  const visibleExpandedItems = useMemo(
    () => (normalizedSearchTerm ? findExpandableItems(filteredOptions) : expandedItems),
    [expandedItems, filteredOptions, normalizedSearchTerm],
  );

  useEffect(() => {
    set_value(value ?? null);
  }, [value]);

  useEffect(() => {
    setExpandedItems((current) => {
      const next = new Set(current);
      findExpandedAncestors(normalizedOptions, _value).forEach((item) => next.add(item));
      return next;
    });
  }, [normalizedOptions, _value]);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      return;
    }

    window.requestAnimationFrame(() => searchInput.current?.focus());

    const keydown = (event: KeyboardEvent) => {
      const optionButtons = Array.from(wrapper.current?.querySelectorAll<HTMLElement>('.tree-option') ?? []);
      const activeElement = document.activeElement as HTMLElement | null;
      const isSearchInput = activeElement === searchInput.current;
      const currentIndex = optionButtons.findIndex((option) => option === document.activeElement);

      switch (event.key) {
        case 'ArrowUp':
          if (isSearchInput) {
            return;
          }

          event.preventDefault();
          optionButtons[currentIndex > 0 ? currentIndex - 1 : optionButtons.length - 1]?.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          optionButtons[
            isSearchInput ? 0 : currentIndex >= 0 && currentIndex < optionButtons.length - 1 ? currentIndex + 1 : 0
          ]?.focus();
          break;
        case 'Enter':
          if (isSearchInput) {
            return;
          }

          if (document.activeElement?.classList.contains('tree-option')) {
            event.preventDefault();
            (document.activeElement as HTMLElement).click();
          }
          break;
      }
    };

    window.addEventListener('keydown', keydown);

    return () => window.removeEventListener('keydown', keydown);
  }, [open]);

  const selectOption = (option: NormalizedDropdownTreeOption) => {
    if (option.disabled) {
      return;
    }

    set_value(option.value);
    const event = { target: { name, value: option.value, label: option.label || null } };
    onChange?.(event);
    setOpen(false);

    if (onBlur) {
      setTimeout(() => {
        onBlur(event);
      }, 0);
    }
  };

  const toggleExpand = useCallback((optionValue: string) => {
    setExpandedItems((current) => {
      const next = new Set(current);

      if (next.has(optionValue)) {
        next.delete(optionValue);
      } else {
        next.add(optionValue);
      }

      return next;
    });
  }, []);

  const handleClear = () => {
    set_value(null);
    onClear?.();
    const event = { target: { name, value: null, label: null } };
    onChange?.(event);
  };

  return (
    <PopoverMenu
      open={open}
      title={selectedOption?.displayLabel || placeholder || ''}
      titleClass={!selectedOption && placeholder ? 'placeholder' : ''}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      className={classNames(
        selectedOption && 'selected',
        '[&_.main-button]:h-11 [&_.main-button]:rounded-[6px] [&_.main-button]:border [&_.main-button]:bg-white [&_.main-button]:px-3 [&_.main-button]:text-sm [&_.main-button]:text-zinc-800',
        '[&_.popover-title]:truncate',
        invalid ? '[&_.main-button]:border-red-500' : '[&_.main-button]:border-zinc-200',
        className,
      )}
      offset={offset || 4}
      placement={placement}
      portal={popoverMenuPortal}
      contentWrapperClassName={contentWrapperClassName}
      showClearIcon={onClear && Boolean(selectedOption)}
      onClearIconClick={handleClear}
    >
      <div ref={wrapper} className="max-h-80 w-[min(360px,calc(100vw-32px))] overflow-y-auto py-2">
        <div className="sticky top-0 z-10 bg-white px-3 pb-2">
          <input
            ref={searchInput}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search"
            className="tree-search-input h-10 w-full rounded-[6px] border border-zinc-200 bg-white px-3 text-sm text-zinc-800 transition outline-none placeholder:text-zinc-400 focus:border-zinc-400"
          />
        </div>

        {filteredOptions.length > 0 ? (
          renderOptions({
            options: filteredOptions,
            selectedValue: _value,
            expandedItems: visibleExpandedItems,
            open,
            onSelect: selectOption,
            onToggleExpand: toggleExpand,
          })
        ) : (
          <div className="px-4 py-3 text-sm text-zinc-500">No results found</div>
        )}
      </div>
    </PopoverMenu>
  );
};

export default FilterableDropdownTree;
